import { Link } from "react-router-dom";

// Hero illustration with actual Figma assets - exact positioning from Figma
// Supports 3 variants: Default (Mark Twain), Variant2 (Napoleon Hill), Variant3 (Mark Twain with reactions)
type HeroIllustrationProps = {
  variant?: "default" | "variant2" | "variant3";
};

const HeroIllustration = ({ variant = "default" }: HeroIllustrationProps) => {
  const isDefault = variant === "default";
  const isVariant2 = variant === "variant2";
  const isVariant3 = variant === "variant3";

  return (
    <div className="relative isolate w-[481px] h-[487px]" style={{ overflow: "visible" }}>
      {/* Layer 1 (back): Book cover - BEHIND the author portrait */}
      <div
        className="absolute z-10 overflow-hidden"
        style={{
          left: "140.5px",
          top: "0px",
          width: "232.5px",
          height: "372px",
          borderRadius: "2px 16px 16px 2px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        <img
          src="/images/hero/book-cover.png"
          alt="The Adventures of Huckleberry Finn by Mark Twain"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Layer 2 (front): Author portrait - IN FRONT of the book */}
      <div
        className="absolute z-20"
        style={{
          left: "78px",
          top: "149px",
          width: "420px",
          height: "338px",
          transform: "rotate(180deg) scaleY(-1)",
        }}
      >
        <img
          src="/images/hero/mark-twain.png"
          alt="Mark Twain"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            mixBlendMode: "multiply",
          }}
        />
      </div>

      {/* Layer 3: Floating card - "Get a summary in seconds" */}
      <div
        className="absolute z-30 backdrop-blur-md bg-white/90 border border-black/[0.08] rounded-xl p-4"
        style={{ left: "-18px", top: "-24px" }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded shrink-0 overflow-hidden">
            <img
              src="/images/hero/document-icon.png"
              alt=""
              className="w-full h-full object-cover"
              aria-hidden="true"
            />
          </div>
          <p className="text-sm font-medium text-[var(--color-text-secondary)] leading-tight w-[109px]">
            Get a summary in seconds
          </p>
        </div>
      </div>

      {/* Layer 3: Message bubble */}
      <div
        className="absolute z-30 backdrop-blur-md bg-white/90 border border-black/[0.08] rounded-xl p-4 w-[286px]"
        style={{ left: "-46px", top: "287px" }}
      >
        <div className="flex items-start gap-3">
          <div className="relative w-10 h-10 rounded-full shrink-0 overflow-hidden border border-black/[0.08]">
            <img
              src="/images/hero/huck-finn-avatar.png"
              alt="Huckleberry Finn"
              className="w-full h-full object-cover"
            />
            <span className="absolute bottom-[-1px] right-[-1px] w-2.5 h-2.5 bg-[#49A878] border-[1.5px] border-white rounded-full" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <p className="text-sm font-medium text-[var(--color-text-secondary)] truncate">
                Huckleberry Fin
              </p>
              <p className="text-xs text-[var(--color-text-secondary)] shrink-0">Friday 2:20pm</p>
            </div>
            <div className="bg-[var(--color-bg-muted)] border border-[var(--color-border-subtle)] rounded-bl-lg rounded-br-lg rounded-tr-lg px-3 py-2">
              <p className="text-base text-[var(--color-text-primary)]">Hey Olivia, how are you?</p>
            </div>
            {/* Reaction buttons - shown in variant3 */}
            {isVariant3 && (
              <div className="flex items-center justify-end gap-1 mt-1.5">
                <button className="flex items-center gap-1 bg-[var(--color-bg-muted)] border border-[var(--color-border-subtle)] rounded-full px-2 py-1 h-6">
                  <span className="text-sm">❤️</span>
                </button>
                <button className="flex items-center gap-1 bg-[var(--color-bg-muted)] border border-[var(--color-border-subtle)] rounded-full px-2 py-1 h-6">
                  <span className="text-sm">👌</span>
                  <span className="text-sm font-medium text-[var(--color-text-secondary)]">2</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Layer 3: Tooltip - "Use Chatable to see Mark Twain thinking" */}
      <div
        className="absolute z-30"
        style={{ left: "311px", top: "98px", transform: "rotate(15deg)" }}
      >
        <div className="flex flex-col items-center shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03),0px_2px_2px_-1px_rgba(10,13,18,0.04)]">
          <div className="bg-[#1E1B19] text-white text-xs font-semibold px-3 py-2 rounded-lg w-[88px] text-center leading-[18px]">
            Use Chatable to see Mark Twain thinking
          </div>
          <div
            className="w-4 h-1.5 bg-[#1E1B19] -mt-0.5"
            style={{ clipPath: "polygon(50% 100%, 0 0, 100% 0)" }}
          />
        </div>
      </div>

      {/* Layer 3: Pagination slider - pill shape at bottom */}
      <div
        className="absolute z-30 flex items-center gap-2 backdrop-blur-sm bg-white/90 rounded-full p-2 w-[160px]"
        style={{ left: "151px", top: "441px" }}
      >
        {/* Active dot changes based on variant */}
        <div
          className={`flex-1 h-1.5 rounded-full ${isDefault ? "bg-[#EC6100]" : "bg-[#E4DED9]"}`}
        />
        <div
          className={`flex-1 h-1.5 rounded-full ${isVariant2 ? "bg-[#EC6100]" : "bg-[#E4DED9]"}`}
        />
        <div
          className={`flex-1 h-1.5 rounded-full ${isVariant3 ? "bg-[#EC6100]" : "bg-[#E4DED9]"}`}
        />
      </div>
    </div>
  );
};

interface HeroProps {
  title?: string;
  subtitle?: string;
  primaryCta?: {
    label: string;
    to: string;
  };
  secondaryCta?: {
    label: string;
    to: string;
  };
  isLoggedIn?: boolean;
}

export default function Hero({
  title = "Bring books to life",
  subtitle = "Chat with characters, get spoiler-safe summaries, and turn any book into a personal reading companion.",
  primaryCta = { label: "Get started", to: "/gallery" },
  isLoggedIn = false,
}: HeroProps) {
  // For logged-in users, we might show a different hero or skip it
  if (isLoggedIn) {
    return null; // Logged-in users will see the book cards section instead
  }

  return (
    <section className="w-full bg-[var(--color-bg-page)]" aria-labelledby="hero-title">
      {/* Full-width background with centered content */}
      <div className="w-full pt-16 pb-0 overflow-visible">
        <div className="max-w-[1280px] mx-auto px-8 overflow-visible">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-8 overflow-visible">
            {/* Left Content - Figma: w-701px, gap-48px between sections */}
            <div className="flex-1 w-full lg:w-[701px] lg:max-w-[701px] py-8 lg:py-[103px]">
              {/* Heading and supporting text - Figma: gap-24px */}
              <div className="flex flex-col gap-6">
                {/* Heading - Figma: 60px semibold, line-height 72px, tracking -1.2px, color #36322f */}
                <h1
                  id="hero-title"
                  className="text-4xl md:text-5xl lg:text-[60px] font-semibold text-[var(--color-text-primary)] leading-tight lg:leading-[72px] tracking-[-1.2px]"
                >
                  {title}
                </h1>

                {/* Supporting text - Figma: 20px REGULAR (not bold), line-height 30px, color #857e76, max-w 560px */}
                <p className="text-lg lg:text-[20px] font-normal text-[var(--color-text-secondary)] leading-relaxed lg:leading-[30px] max-w-[560px]">
                  {subtitle}
                </p>
              </div>

              {/* CTA Section - Figma: gap-8px, mt-48px */}
              <div className="mt-12 flex flex-col gap-2">
                {/* Primary CTA Button - Figma: w-320px, px-18px py-12px, rounded-8px */}
                <Link
                  to={primaryCta.to}
                  className="relative inline-flex items-center justify-center w-full max-w-[320px] px-[18px] py-3 text-[16px] font-semibold leading-6 text-[#2C1409] bg-[#FF6900] rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] hover:bg-[#EC6100] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF6900] focus:ring-offset-2 overflow-hidden"
                >
                  {primaryCta.label}
                  {/* Skeuomorphic inner shadow */}
                  <span className="absolute inset-0 pointer-events-none rounded-lg shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)]" />
                </Link>

                {/* Footer disclaimer text - Figma: 14px regular, line-height 20px, color #857e76 */}
                <p className="text-[14px] font-normal leading-5 text-[var(--color-text-secondary)]">
                  *Spoiler-safe · Respects your progress · Copyright-respecting
                </p>
              </div>
            </div>

            {/* Right Illustration - Figma: w-481px h-487px */}
            <div className="shrink-0 w-[481px] h-[487px] hidden lg:block relative overflow-visible">
              <HeroIllustration />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
